import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  Delete,
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
  Pending,
  Info,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import api from '../utils/api';

interface UploadHistory {
  id: number;
  filename: string;
  original_name: string;
  status: string;
  records_added?: number;
  records_skipped?: number;
  error_message?: string;
  created_at: string;
  uploaded_by: string;
}

interface Period {
  id: number;
  month_name: string;
  year: number;
  is_active: number;
}

const CalendarUpload: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [history, setHistory] = useState<UploadHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(1);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedRecords, setExpandedRecords] = useState<Record<number, any[]>>({});
  const [loadingRecords, setLoadingRecords] = useState<Record<number, boolean>>({});

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get('/upload/history');
      const data = response.data || response;
      setHistory(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Ошибка загрузки истории:', err);
      setError('Ошибка загрузки истории');
    } finally {
      setLoading(false);
    }
  };

  const loadPeriods = async () => {
    try {
      const data = await api.get('/periods');
      const activePeriods = Array.isArray(data) ? data.filter((p: Period) => p.is_active === 1) : [];
      setPeriods(activePeriods);
      if (activePeriods.length > 0) {
        setSelectedPeriod(activePeriods[0].id);
      }
    } catch (err) {
      console.error('Ошибка загрузки периодов:', err);
    }
  };

  useEffect(() => {
    loadHistory();
    loadPeriods();
  }, []);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(acceptedFiles);
    setError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: true,
  });

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Выберите файлы для загрузки');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    
    files.forEach((file) => {
      formData.append('files', file);
    });

    try {
      const response = await axios.post('http://192.168.50.43:5002/api/upload/calendar', formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setUploadProgress(percentCompleted);
        },
      });

      setSuccess(`Загружено ${files.length} файлов. Добавлено записей: ${response.data.total_records || 0}`);
      setFiles([]);
      await loadHistory();
    } catch (err: any) {
      console.error('Ошибка загрузки:', err);
      setError(err.response?.data?.error || err.message || 'Ошибка загрузки файлов');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Очистить всю историю загрузок?')) return;
    try {
      await api.delete('/upload/history');
      setHistory([]);
      setSuccess('История очищена');
    } catch (err) {
      console.error('Ошибка очистки истории:', err);
      setError('Ошибка очистки истории');
    }
  };

  const handleDeleteHistory = async (id: number) => {
    if (!window.confirm('Удалить запись и файл?')) return;
    try {
      setDeleting(id);
      await api.delete(`/upload/history/${id}`);
      await loadHistory();
      setSuccess('Запись и файл удалены');
    } catch (err: any) {
      console.error('Ошибка удаления:', err);
      setError('Ошибка удаления записи');
    } finally {
      setDeleting(null);
    }
  };

  const loadRecords = async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }

    if (expandedRecords[id]) {
      setExpandedId(id);
      return;
    }

    setLoadingRecords(prev => ({ ...prev, [id]: true }));
    try {
      const response = await api.get(`/upload/history/${id}/records`);
      const data = response.data || response;
      setExpandedRecords(prev => ({ ...prev, [id]: data.records || [] }));
      setExpandedId(id);
    } catch (err) {
      console.error('Ошибка загрузки записей:', err);
      setError('Ошибка загрузки записей');
    } finally {
      setLoadingRecords(prev => ({ ...prev, [id]: false }));
    }
  };

  const getStatusChip = (status: string) => {
    const statusMap: Record<string, { label: string; color: any; icon: any }> = {
      'success': { label: 'Успешно', color: 'success', icon: <CheckCircle /> },
      'completed': { label: 'Завершено', color: 'success', icon: <CheckCircle /> },
      'pending': { label: 'В очереди', color: 'default', icon: <Pending /> },
      'processing': { label: 'Обработка', color: 'warning', icon: <Pending /> },
      'error': { label: 'Ошибка', color: 'error', icon: <ErrorIcon /> },
    };
    const info = statusMap[status] || statusMap['pending'];
    return <Chip label={info.label} color={info.color as any} size="small" icon={info.icon} />;
  };

  const formatDate = (date: string) => {
    if (!date) return '—';
    try {
      return new Date(date).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return date;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CloudUpload sx={{ fontSize: 32, color: 'primary.main' }} />
                <Box>
                  
                         <Typography variant="subtitle2" color="text.secondary">Колличество календарей: {history.length} </Typography>                </Box>
              </Box>
             </Box>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper
            {...getRootProps()}
            sx={{
              p: 4,
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 3,
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s',
              height: 280,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <input {...getInputProps()} />
            <CloudUpload sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Отпустите файлы для загрузки' : 'Выберите файлы Excel'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Поддерживаются .xlsx и .xls (можно выбрать несколько)
            </Typography>
          </Paper>

          {periods.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Период</InputLabel>
                <Select
                  value={selectedPeriod}
                  label="Период"
                  onChange={(e) => setSelectedPeriod(Number(e.target.value))}
                >
                  {periods.map((period) => (
                    <MenuItem key={period.id} value={period.id}>
                      {period.month_name} {period.year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ 
            height: 280,
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            borderRadius: 3,
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Info sx={{ fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Информация
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                    Формат файлов
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Excel (.xlsx, .xls)
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                    Требуемые колонки
                  </Typography>
                  <Typography variant="body2">• Дата (DD.MM.YYYY)</Typography>
                  <Typography variant="body2">• Экскурсовод</Typography>
                  <Typography variant="body2">• Программа</Typography>
                  <Typography variant="body2">• Время</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {files.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ mt: 2, p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Выбрано файлов: {files.length}
              </Typography>
              <List dense>
                {files.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <InsertDriveFile />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={`${(file.size / 1024).toFixed(1)} KB`}
                    />
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setFiles(files.filter((_, i) => i !== index));
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={uploading}
                  startIcon={<CloudUpload />}
                >
                  Загрузить
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setFiles([])}
                  disabled={uploading}
                >
                  Очистить список
                </Button>
              </Box>
              {uploading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Загрузка: {uploadProgress}%
                  </Typography>
                </Box>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {success}
                </Alert>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      <Paper sx={{ mt: 3, p: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            История загрузок
            {history.length > 0 && (
              <Chip label={history.length} size="small" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Обновить">
              <span>
                <IconButton onClick={loadHistory} disabled={loading} size="small">
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleClearHistory}
              disabled={history.length === 0}
            >
              Очистить всё
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : history.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary">История загрузок пуста</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Детали</TableCell>
                  <TableCell>Файл</TableCell>
                  <TableCell align="center">Статус</TableCell>
                  <TableCell align="center">Записей</TableCell>
                  <TableCell align="right">Дата</TableCell>
                  <TableCell align="center">Действие</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item) => (
                  <React.Fragment key={item.id}>
                    <TableRow hover>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => loadRecords(item.id)}
                          disabled={loadingRecords[item.id]}
                        >
                          {expandedId === item.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{item.original_name || item.filename}</TableCell>
                      <TableCell align="center">{getStatusChip(item.status)}</TableCell>
                      <TableCell align="center">{item.records_added || 0}</TableCell>
                      <TableCell align="right">{formatDate(item.created_at)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteHistory(item.id)}
                          disabled={deleting === item.id}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0 }}>
                        <Collapse in={expandedId === item.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Записи из файла:
                              {loadingRecords[item.id] && <CircularProgress size={16} sx={{ ml: 1 }} />}
                            </Typography>
                            {!loadingRecords[item.id] && expandedRecords[item.id] && (
                              expandedRecords[item.id].length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                  Нет записей
                                </Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Дата</TableCell>
                                      <TableCell>Гид</TableCell>
                                      <TableCell>Программа</TableCell>
                                      <TableCell>Время</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {expandedRecords[item.id].map((rec: any) => (
                                      <TableRow key={rec.id}>
                                        <TableCell>{rec.date}</TableCell>
                                        <TableCell>{rec.guide_name}</TableCell>
                                        <TableCell>{rec.tour_name}</TableCell>
                                        <TableCell>{rec.time || '—'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default CalendarUpload;
